import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ThermostatPollHealthResponse, ThermostatPollHealthRow, ThermostatPollRunResult } from "./types";
import { formatAgeMs } from "./helpers";

export function ThermostatPollHealthPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const healthQuery = useQuery<ThermostatPollHealthResponse>({
    queryKey: ["/api/admin/thermostat-poll-health"],
    queryFn: async () => {
      const res = await fetch("/api/admin/thermostat-poll-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load thermostat poll health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  // [Task #1314] Trigger the same in-process poller the cron uses so admins
  // can verify a fix immediately. After it returns, refetch the health table
  // so freshly-recovered users flip to "healthy" without waiting a minute.
  const runMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/thermostat-poll-health/run", {});
      return (await res.json()) as ThermostatPollRunResult;
    },
    onSuccess: (result) => {
      toast({
        title: "Poll run complete",
        description:
          `Inserted ${result.readingsInserted} reading${result.readingsInserted === 1 ? "" : "s"}, ` +
          `created ${result.healthAlertsCreated} health alert${result.healthAlertsCreated === 1 ? "" : "s"}, ` +
          `${result.errors.length} error${result.errors.length === 1 ? "" : "s"}.`,
        variant: result.errors.length > 0 ? "destructive" : "default",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/thermostat-poll-health"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Poll run failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  function statusBadge(status: ThermostatPollHealthRow["status"]) {
    if (status === "healthy") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="badge-thermostat-poll-status-healthy">
          Healthy
        </Badge>
      );
    }
    if (status === "stalled") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="badge-thermostat-poll-status-stalled">
          Stalled
        </Badge>
      );
    }
    if (status === "error") {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="badge-thermostat-poll-status-error">
          Error
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" data-testid="badge-thermostat-poll-status-no-connection">
        No connection
      </Badge>
    );
  }

  const data = healthQuery.data;
  const thresholdHours = data ? Math.round(data.stallThresholdMs / 3600000) : 2;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-thermostat-poll-health-title">
            Thermostat Polling Health
          </h3>
          <p className="text-sm text-muted-foreground">
            Per-user view of the thermostat poll cron. A user is "stalled" when their most recent thermostat reading is older than {thresholdHours} hour{thresholdHours === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            data-testid="button-run-thermostat-poll-now"
          >
            {runMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Run poll now
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => healthQuery.refetch()}
            disabled={healthQuery.isFetching}
            data-testid="button-refresh-thermostat-poll-health"
          >
            {healthQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {healthQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : healthQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-thermostat-poll-health-error">
            {parseErrorMessage(healthQuery.error, "Failed to load thermostat poll health")}
          </p>
        </Card>
      ) : data ? (
        <>
          <Card className="p-6" data-testid="card-thermostat-poll-health-summary">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-semibold tabular-nums" data-testid="text-thermostat-poll-health-healthy-count">
                  {data.summary.healthy}
                </span>
                <span className="text-sm text-muted-foreground">healthy</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="font-semibold tabular-nums" data-testid="text-thermostat-poll-health-stalled-count">
                  {data.summary.stalled}
                </span>
                <span className="text-sm text-muted-foreground">stalled</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <span className="font-semibold tabular-nums" data-testid="text-thermostat-poll-health-no-connection-count">
                  {data.summary.no_connection}
                </span>
                <span className="text-sm text-muted-foreground">no connection</span>
              </div>
              {data.summary.error > 0 ? (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-semibold tabular-nums" data-testid="text-thermostat-poll-health-error-count">
                    {data.summary.error}
                  </span>
                  <span className="text-sm text-muted-foreground">error</span>
                </div>
              ) : null}
              <span className="text-xs text-muted-foreground ml-auto" data-testid="text-thermostat-poll-health-generated-at">
                Updated {new Date(data.generatedAt).toLocaleTimeString()}
              </span>
            </div>
          </Card>

          <Card className="p-0" data-testid="card-thermostat-poll-health-table">
            {data.users.length === 0 ? (
              <p className="text-sm text-muted-foreground p-6" data-testid="text-thermostat-poll-health-empty">
                No active thermostat connections.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Connections</TableHead>
                    <TableHead>Last reading</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.users.map((row) => (
                    <TableRow key={row.userId} data-testid={`row-thermostat-poll-health-${row.userId}`}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium" data-testid={`text-thermostat-poll-health-email-${row.userId}`}>
                            {row.email ?? `User #${row.userId}`}
                          </span>
                          {row.name ? (
                            <span className="text-xs text-muted-foreground" data-testid={`text-thermostat-poll-health-name-${row.userId}`}>
                              {row.name}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums" data-testid={`text-thermostat-poll-health-connection-count-${row.userId}`}>
                        {row.connectionCount}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`text-thermostat-poll-health-last-reading-${row.userId}`}>
                        {row.lastReadingAt ? new Date(row.lastReadingAt).toLocaleString() : "Never"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground" data-testid={`text-thermostat-poll-health-age-${row.userId}`}>
                        {formatAgeMs(row.ageMs)}
                      </TableCell>
                      <TableCell data-testid={`cell-thermostat-poll-health-status-${row.userId}`}>
                        <div className="flex flex-col gap-1">
                          {statusBadge(row.status)}
                          {row.error ? (
                            <span
                              className="text-xs text-red-600"
                              data-testid={`text-thermostat-poll-health-error-${row.userId}`}
                            >
                              {row.error}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
